import QRCode from 'react-qr-code';
import { cn } from "@/lib/utils";
import { BadgePill } from './BadgePill';
import { Calendar, MapPin, Clock, Shield } from 'lucide-react';

// Status colors for passes
const statusColors = {
  valid: 'primary',
  used: 'success',
  revoked: 'error',
  expired: 'default',
};

const statusLabels = {
  valid: 'VALID',
  used: 'CHECKED IN',
  revoked: 'REVOKED',
  expired: 'EXPIRED',
};

export const PassCard = ({
  guestName,
  eventName,
  eventDate,
  eventLocation,
  qrCodeData,
  status = 'valid',
  checkedInAt,
  createdAt,
  size = 'lg',
  className,
}) => {
  const qrSizes = {
    sm: 120,
    md: 160,
    lg: 200,
    xl: 240,
  };
  
  const qrSize = qrSizes[size] || qrSizes.lg;
  
  return (
    <div 
      className={cn(
        "bg-gradient-to-b from-graphite to-obsidian rounded-2xl border border-white/[0.08] overflow-hidden",
        className
      )}
      data-testid="pass-card"
    >
      {/* Header with gradient */}
      <div className="bg-gradient-to-r from-electric-ice-blue/20 to-cool-mint/10 px-6 py-4 border-b border-white/[0.06]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <Shield className="w-5 h-5 text-electric-ice-blue" />
            </div>
            <div>
              <p className="text-xs text-soft-silver uppercase tracking-wider">Guest Pass</p>
              <p className="text-lg font-semibold text-cloud-white">{guestName}</p>
            </div>
          </div>
          <BadgePill variant={statusColors[status]}>
            {statusLabels[status] || status.toUpperCase()}
          </BadgePill>
        </div>
      </div>
      
      {/* QR Code Section */}
      <div className="px-6 py-6 flex flex-col items-center">
        <div className="bg-white rounded-xl p-4 mb-4 shadow-lg">
          {qrCodeData ? (
            <QRCode
              value={qrCodeData}
              size={qrSize}
              level="H"
              bgColor="#ffffff"
              fgColor="#0A0A0B"
            />
          ) : (
            <div 
              style={{ width: qrSize, height: qrSize }}
              className="flex items-center justify-center bg-gray-100 rounded-lg"
            >
              <p className="text-gray-400 text-sm">No QR Data</p>
            </div>
          )}
        </div>
        
        {/* QR Code ID */}
        {qrCodeData && (
          <p className="text-xs text-muted-text font-mono tracking-wider">
            {qrCodeData.slice(-12).toUpperCase()}
          </p>
        )}
      </div>
      
      {/* Event Details */}
      <div className="px-6 pb-6 space-y-3">
        <div className="flex items-center gap-3 text-soft-silver">
          <Calendar className="w-4 h-4 text-electric-ice-blue flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-cloud-white truncate">{eventName}</p>
            {eventDate && (
              <p className="text-xs text-muted-text font-mono">{eventDate}</p>
            )}
          </div>
        </div>
        
        {eventLocation && (
          <div className="flex items-center gap-3 text-soft-silver">
            <MapPin className="w-4 h-4 text-electric-ice-blue flex-shrink-0" />
            <p className="text-sm truncate">{eventLocation}</p>
          </div>
        )}
        
        {checkedInAt && (
          <div className="flex items-center gap-3 text-cool-mint">
            <Clock className="w-4 h-4 flex-shrink-0" />
            <p className="text-sm font-mono">Checked in: {checkedInAt}</p>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="px-6 py-3 bg-white/[0.02] border-t border-white/[0.06] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-electric-ice-blue flex items-center justify-center">
            <span className="text-white font-bold text-xs">C</span>
          </div>
          <span className="text-xs text-muted-text">CamPassed</span>
        </div>
        {createdAt && (
          <p className="text-xs text-muted-text font-mono">
            Generated: {createdAt}
          </p>
        )}
      </div>
    </div>
  );
};

export default PassCard;
